// ────────────────────────────────────────────────────────────────────────────
// Document layouts, from the server.
//
// Replaces the localStorage store. Two things make this more than a swap:
//
//  1. **Stock templates are code, not rows.** "Stratum Standard" ships in the
//     app and is what prints when an organisation has no layout of its own, so
//     the list is server rows *plus* the shipped ones, and a stock template is
//     never saved — customising starts with a duplicate.
//
//  2. **`layoutJson` is opaque on the wire.** Directory stores and versions it
//     without parsing; this is where it becomes a `DocumentLayout`, migrated
//     forward from whatever `schemaVersion` it was saved in. A row that cannot
//     be parsed is dropped from the list rather than crashing the page — one
//     bad layout must not take the screen down.
//
// The action surface is the same as the store it replaces, so the pages above
// barely changed.
// ────────────────────────────────────────────────────────────────────────────
import {
  useCreateOrganisationDocumentTemplate,
  useDeleteOrganisationDocumentTemplate,
  useOrganisationDocumentTemplates,
  useSetDefaultOrganisationDocumentTemplate,
  useUpdateOrganisationDocumentTemplate,
} from '@my/directory-api/hooks';
import type { OrganisationDocumentTemplate as TemplateRow } from '@my/directory-api';
import { useCallback, useMemo } from 'react';
import { dedupeLayout, migrateLayout } from './migrateLayout';
import { buildStockTemplates, STOCK_TEMPLATE_IDS } from './stockTemplates';
import {
  DOC_TYPES,
  LAYOUT_SCHEMA_VERSION,
  type DocumentLayout,
  type DocumentTemplate,
  type DocumentType,
} from './types';

/** The app these layouts belong to. Depot would pass its own. */
const APP = 'Tms' as const;

/** A `docType` we do not recognise is ignored — probably another app's. */
function toDocumentType(docType: string): DocumentType | null {
  return (DOC_TYPES as readonly string[]).includes(docType) ? (docType as DocumentType) : null;
}

/** Parse + migrate one row. Returns null when the layout will not parse. */
function toTemplate(row: TemplateRow): DocumentTemplate | null {
  const docType = toDocumentType(row.docType);
  if (!docType) return null;
  try {
    const parsed = JSON.parse(row.layoutJson) as DocumentLayout;
    return {
      id: row.id,
      docType,
      name: row.name,
      isDefault: row.isDefault,
      isStock: false,
      schemaVersion: LAYOUT_SCHEMA_VERSION,
      updatedUtc: row.updatedUtc ?? row.createdUtc,
      layout: dedupeLayout(migrateLayout(parsed, row.schemaVersion), { warn: true }),
    };
  } catch {
    // Unparseable JSON in one row must not blank the whole list.
    return null;
  }
}

export interface DocumentTemplatesApi {
  templates: DocumentTemplate[];
  isLoading: boolean;
  error: Error | null;
  /** True while a write is in flight — the pages disable their buttons on it. */
  isSaving: boolean;

  createTemplate: (docType: DocumentType, name: string) => Promise<string | null>;
  /** Create from a layout that already exists — used by the localStorage lift. */
  createFromLayout: (
    docType: DocumentType,
    name: string,
    layout: DocumentLayout,
    schemaVersion: number,
  ) => Promise<string | null>;
  duplicateTemplate: (id: string) => Promise<string | null>;
  /** Rename + re-save. One call, because the server PUT takes both. */
  saveTemplate: (id: string, name: string, layout: DocumentLayout) => Promise<void>;
  setDefault: (id: string) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  /** Re-read the layouts from the server, ignoring the cached copy. */
  refresh: () => Promise<void>;
}

export function useDocumentTemplates(organisationId: string | null): DocumentTemplatesApi {
  const query = useOrganisationDocumentTemplates(organisationId, APP);
  const create = useCreateOrganisationDocumentTemplate();
  const update = useUpdateOrganisationDocumentTemplate();
  const promote = useSetDefaultOrganisationDocumentTemplate();
  const remove = useDeleteOrganisationDocumentTemplate();

  const templates = useMemo(() => {
    const saved = (query.data ?? [])
      .map(toTemplate)
      .filter((t): t is DocumentTemplate => t !== null);

    // A stock template is the default only while the organisation has not made
    // one of its own the default for that kind — otherwise both would claim it.
    const claimed = new Set(saved.filter((t) => t.isDefault).map((t) => t.docType));
    const stock = buildStockTemplates().map((t) => ({
      ...t,
      isDefault: !claimed.has(t.docType),
    }));

    return [...stock, ...saved];
  }, [query.data]);

  // Depends on `refetch`, not on `query` — the query object is a new identity
  // every render, which would make this callback churn for no reason.
  const { refetch } = query;
  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const findById = useCallback(
    (id: string) => templates.find((t) => t.id === id) ?? null,
    [templates],
  );

  const createFromLayout = useCallback(
    async (
      docType: DocumentType,
      name: string,
      layout: DocumentLayout,
      schemaVersion: number,
    ) => {
      if (!organisationId) return null;
      return await create.mutateAsync({
        organisationId,
        app: APP,
        docType,
        name,
        layoutJson: JSON.stringify(dedupeLayout(layout)),
        schemaVersion,
      });
    },
    [organisationId, create],
  );

  const createTemplate = useCallback(
    async (docType: DocumentType, name: string) => {
      const stock = buildStockTemplates().find((t) => t.docType === docType);
      if (!stock) return null;
      return await createFromLayout(docType, name, stock.layout, LAYOUT_SCHEMA_VERSION);
    },
    [createFromLayout],
  );

  const duplicateTemplate = useCallback(
    async (id: string) => {
      if (!organisationId) return null;
      const source = findById(id);
      if (!source) return null;
      return await create.mutateAsync({
        organisationId,
        app: APP,
        docType: source.docType,
        name: `${source.name} (copy)`,
        layoutJson: JSON.stringify(dedupeLayout(source.layout)),
        schemaVersion: LAYOUT_SCHEMA_VERSION,
        // A copy never steals the default — promoting is a separate, deliberate
        // act, and duplicating to experiment must not change what customers get.
        isDefault: false,
      });
    },
    [organisationId, create, findById],
  );

  const saveTemplate = useCallback(
    async (id: string, name: string, layout: DocumentLayout) => {
      // Stock layouts are shipped code; there is no row to write to.
      if (Object.values(STOCK_TEMPLATE_IDS).includes(id)) return;
      await update.mutateAsync({
        templateId: id,
        name,
        layoutJson: JSON.stringify(dedupeLayout(layout)),
        schemaVersion: LAYOUT_SCHEMA_VERSION,
      });
    },
    [update],
  );

  const setDefault = useCallback(
    async (id: string) => {
      // Promoting the stock template means "stop using ours", which the server
      // has no row for. Clearing an organisation's default is not something the
      // UI offers, so this is only reachable by mistake.
      if (Object.values(STOCK_TEMPLATE_IDS).includes(id)) return;
      await promote.mutateAsync(id);
    },
    [promote],
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      if (Object.values(STOCK_TEMPLATE_IDS).includes(id)) return;
      await remove.mutateAsync(id);
    },
    [remove],
  );

  return {
    templates,
    isLoading: query.isPending,
    error: query.error ?? null,
    isSaving: create.isPending || update.isPending || promote.isPending || remove.isPending,
    createTemplate,
    createFromLayout,
    duplicateTemplate,
    saveTemplate,
    setDefault,
    deleteTemplate,
    refresh,
  };
}
